<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ title }}</b-card-title>
      <div class="d-flex align-items-center">
        <b-button
          v-if="showUpdateButton"
          variant="secondary"
          :disabled="isLoading"
          style="margin-right: 20px;"
          @click="() => forceUpdate = true"
        >
          <b-spinner
            v-if="isLoading"
            variant="light"
            small
            label="Spinning"
          />
          Päivitä tilasto
        </b-button>
        <b-form-group
          v-for="selector in selectors"
          :key="selector.id"
          :label="selector.title"
          :style="{marginRight: '20px', width: selector.width}"
        >
          <v-select
            v-if="selector.options"
            v-model="selector.selected"
            :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
            :clearable="selector.selectorOptions.clearable"
            :multiple="selector.selectorOptions.multiple"
            :options="selector.options"
            :reduce="option => option.value"
            style="text-transform: capitalize;"
            @input="(value) => selectorChanged(value, selector.id)"
          />
        </b-form-group>
        <div class="d-flex align-items-center" style="margin-top: 10px;">
          <feather-icon
            icon="CalendarIcon"
            size="16"
          />
          <b-dropdown
            :text="selected.text"
            variant="transparent"
            class="weeks-dropdown"
            right
            size="sm"
          >
            <b-dropdown-item
              v-for="option in dropdownOptions"
              :key="`${option.text}`"
              @click="selected = option"
            >
              {{ option.text }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </b-card-header>
    <div style="display: flex; flex-direction: row; justify-content: flex-end;">
      <b-form-group
        v-if="selected.text === 'Valitse ajanjakso'"
        label="Alkaa"
        style="margin-right: 20px; width: 150px;"
      >
        <flat-pickr
          class="form-control"
          :config="{defaultDate: selected.startDate.toDate()}"
          @input="(date) => {
            selected.startDate = new Date(`${date} 00:00:00`)
            forceUpdate = true
          }"
        />
      </b-form-group>
      <b-form-group
        v-if="selected.text === 'Valitse ajanjakso'"
        label="Loppuu"
        style="margin-right: 50px; width: 150px;"
      >
        <flat-pickr
          class="form-control"
          :config="{defaultDate: selected.endDate.toDate()}"
          @input="(date) => {
            selected.endDate = new Date(`${date} 00:00:00`)
            forceUpdate = true
          }"
        />
      </b-form-group>
    </div>
    <!-- chart -->
    <b-card-body>
      <div
        v-if="isLoading"
        class="d-flex justify-content-center"
      >
        <span>
          <b-spinner
            variant="primary"
            label="Spinning"
          />
        </span>
      </div>
      <div v-else class="container">
        <div v-for="chart in charts" :key="chart.id" class="item" :style="{width: chart.size && chart.size === 'lg' ? '50%' : '25%'}">
          <div>
            <div style="display:flex; flex-direction: row; justify-content: space-between; margin-bottom: 30px;">
              <div class="transaction-title">
                {{ chart.title }}
              </div>
              <div>
                <b-dropdown
                  text="Lataa"
                  variant="transparent"
                  right
                  size="sm"
                >
                  <template #button-content>
                    <span class="mr-1">Lataa</span>
                    <feather-icon
                      icon="DownloadIcon"
                      size="16"
                    />
                  </template>
                  <b-dropdown-item
                    @click="downloadCSV(chart)"
                  >
                    CSV
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <BarChart
              v-if="chart.type === 'bar'"
              :chart-options="chartOptions"
              :chart-data="chart.chartData"
            />
            <PieChart
              v-if="chart.type === 'pie'"
              :chart-options="chartOptions"
              :chart-data="chart.chartData"
            />
            <LineChart
              v-if="chart.type === 'line'"
              :chart-options="chartOptions"
              :chart-data="chart.chartData"
            />
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>
    
    <script>
    import {
      BCard, BCardHeader, BCardBody, BCardTitle, BDropdown, BDropdownItem, BSpinner, BFormGroup, BButton,
    } from 'bootstrap-vue'
    import moment from 'moment'
    import BarChart from '@hw-ui/components/charts/chartjs/BarChart.vue'
    import PieChart from '@hw-ui/components/charts/chartjs/PieChart.vue'
    import LineChart from '@hw-ui/components/charts/chartjs/LineChart.vue'
    import vSelect from 'vue-select'
    import flatPickr from 'vue-flatpickr-component'
  
    export default {
      components: {
        BCard,
        BCardHeader,
        BCardBody,
        BCardTitle,
        BDropdown,
        BDropdownItem,
        BSpinner,
        BarChart,
        PieChart,
        LineChart,
        BFormGroup,
        BButton,
        vSelect,
        flatPickr,
      },
      props: {
        title: {
          type: String,
          default: '',
        },
        charts: {
          type: Array,
          default: () => [],
        },
        isLoading: {
          type: Boolean,
          default: false,
        },
        selectors: {
          type: Array,
          default: () => [],
        },
      },
      data() {
        return {
          chartOptions: {
            responsive: true,
            maintainAspectRatio: false
          },
          forceUpdate: false,
          weekNumber: `${this.$t('dashboard.applicationUpdates.title.week')}: ${moment().isoWeek()}, ${moment().isoWeek(moment().isoWeek()).startOf('isoWeek').format('DD.MM.YYYY')} - ${moment().isoWeek(moment().isoWeek()).endOf('isoWeek').format('DD.MM.YYYY')}`,
          selected: {
            text: this.$t('dashboard.this_week'), startDate: moment().isoWeek(moment().isoWeek()).startOf('isoWeek'), endDate: moment().isoWeek(moment().isoWeek()).endOf('isoWeek'), type: 'week',
          },
          selector: {
            selected: null,
            selectorId: null,
          },
        }
      },
      computed: {
        showUpdateButton() {
          return this.selected.endDate <= new Date()
        },
        dropdownOptions() {
          const getWeekRange = date => {
            const weekNumber = moment(date).isoWeek()
            const startDate = moment()
              .isoWeek(weekNumber)
              .startOf('isoWeek')
            const endDate = moment()
              .isoWeek(weekNumber)
              .endOf('isoWeek')
    
            return { startDate, endDate }
          }
          const getMonthRange = date => {
            const startDate = moment(date)
              .startOf('month')
            const endDate = moment(date)
              .endOf('month')
            return { startDate, endDate }
          }
          const getYearRange = date => {
            const startDate = moment(date)
              .startOf('year')
            const endDate = moment(date)
              .endOf('year')
            return { startDate, endDate }
          }
          const now = new Date()
          const currentWeek = getWeekRange(now)
          const weekBefore = getWeekRange(moment().subtract(1, 'weeks').startOf('isoWeek').toDate())
          const currentMonth = getMonthRange(now)
          const monthBefore = getMonthRange(moment().subtract(1, 'months').startOf('month').toDate())
          const currentYear = getYearRange(now)
          const yearBefore = getYearRange(moment().subtract(1, 'years').startOf('year').toDate())
          const list = [
            {
              text: 'Valitse ajanjakso',
              startDate: currentWeek.startDate,
              endDate: currentWeek.endDate,
              type: 'week',
            },
            {
              text: this.$t('dashboard.this_week'),
              startDate: currentWeek.startDate,
              endDate: currentWeek.endDate,
              type: 'week',
            },
            {
              text: 'Viime viikko',
              startDate: weekBefore.startDate,
              endDate: weekBefore.endDate,
              type: 'week',
            },
            {
              text: 'Tämä kuukausi',
              startDate: currentMonth.startDate,
              endDate: currentMonth.endDate,
              type: 'month',
            },
            {
              text: 'Viime kuukausi',
              startDate: monthBefore.startDate,
              endDate: monthBefore.endDate,
              type: 'month',
            },
            {
              text: 'Tämä vuosi',
              startDate: currentYear.startDate,
              endDate: currentYear.endDate,
              type: 'year',
            },
            {
              text: 'Viime vuosi',
              startDate: yearBefore.startDate,
              endDate: yearBefore.endDate,
              type: 'year',
            },
          ]
          const start = moment().startOf('year')
          const months = []
          while (start.isBefore(currentMonth.startDate)) {
            const formatMonth = moment(start)
            const range = getMonthRange(formatMonth)
            months.push({
              text: formatMonth.format('MMM'), startDate: range.startDate, endDate: range.endDate, type: 'month',
            })
            start.add(1, 'month')
          }
          return [...list, ...months.reverse()]
        },
        weekOptions() {
          const start = moment().startOf('year')
          const end = moment().isoWeek(moment().isoWeek()).endOf('isoWeek')
          const weeks = []
    
          while (start.isSameOrBefore(end)) {
            const formatWeek = moment(start).isoWeek()
            if (!weeks.includes(formatWeek) && formatWeek <= moment().isoWeek()) {
              weeks.push(formatWeek)
            }
            start.add(1, 'isoWeeks')
          }
    
          const sortedWeekOptions = weeks.sort((a, b) => a - b)
    
          for (let i = 0; i < sortedWeekOptions.length; i += 1) {
            const weekNumber = sortedWeekOptions[i]
            sortedWeekOptions[i] = `${this.$t('dashboard.applicationUpdates.title.week')}: ${weekNumber}, ${moment().isoWeek(weekNumber).startOf('isoWeek').format('DD.MM.YYYY')} - ${moment().isoWeek(weekNumber).endOf('isoWeek').format('DD.MM.YYYY')}`
          }
    
          return weeks.sort((a, b) => a - b)
        },
      },
      watch: {
        selected() {
          this.setPeriod()
        },
        forceUpdate(bool) {
          if(bool) {
            this.setPeriod()
          }
        }
      },
      mounted() {
        this.setPeriod()
      },
      methods: {
        selectorChanged(value, selectorId) {
          // Reset lower selectors after the changed selector
          this.selectors.forEach((selector, index) => {
            if(selector.id > selectorId) {
              this.selectors[index].selected = this.selectors[index].default || null
            }
          })
          this.setPeriod()
        },
        downloadCSV(chart) {
          const {datasets} = chart.chartData
          let csv = ''
          if(chart.type !== 'pie') {
            csv += ` ;${chart.chartData.labels.join(';')};\n`
            datasets.forEach(dataset => {
            csv += `${dataset.label};`
            dataset.data.forEach(data => {
              csv += `${parseFloat(data).toFixed(3)};`
            })
            csv += '\n'
          })
          } else {
            datasets.forEach(dataset => {
              dataset.data.forEach((data, index) => {
                csv += `${chart.chartData.labels[index]};`
                csv += `${parseFloat(data).toFixed(3)};`
                csv += '\n'
              })
            })
          }
          const unit = this.selectors[2].selected
          const period = `${this.selected.startDate.format('DD.MM.YYYY')  } - ${  this.selected.endDate.format('DD.MM.YYYY')}`
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          const url = URL.createObjectURL(blob)
          link.setAttribute('href', url)
          link.setAttribute('download', `${chart.title}_${period}_${unit}.csv`)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

        },
        setPeriod() {
          this.selected.startDate = moment(this.selected.startDate)
          this.selected.endDate = moment(this.selected.endDate)
          this.$emit('set-period', {
            period: this.selected,
            selected: this.selectors.map(selector => selector.selected),
            force: this.selected.endDate.toDate() > new Date() || this.forceUpdate,
            interval: this.selected.text === 'Tämä vuosi' || this.selected.text === 'Viime vuosi'
              ? 'month'
              : 'day'
          })
          this.forceUpdate = false
        },
      },
    }
    </script>
    
    <style lang="scss">
    @import "@hw-ui/scss/vue/libs/vue-flatpicker.scss";
    </style>

    <style lang="scss">
    @import '@hw-ui/scss/vue/libs/vue-select.scss';
    </style>
    
    <style lang="scss" scoped>
    .container {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      width: 100%;
    }

    .item {
        min-height: 100px;
        margin: 8px;
        border: 1px solid #ccc;
        border-radius: 5px;
        padding: 10px;
        flex:auto;
    }
    ::v-deep .weeks-dropdown {
      background-color: pink;
      .dropdown-menu {
        max-height: 300px;
        overflow-y: auto;
      }
    }
    </style>
    